import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";

/**
 * A flag e o fato de ela ainda nao ter sido lida.
 *
 * `useFeatureFlag` devolve `false` enquanto a consulta corre, e isso e' o
 * comportamento certo para quem so' liga ou desliga um pedaco de tela: na
 * duvida, nao mostrar. Mas para quem ESCOLHE ENTRE DUAS TELAS, "ainda nao sei"
 * lido como "desligada" faz a tela antiga aparecer por um instante e ser
 * substituida — o usuario ve um piscar, e um teste que clique nesse intervalo
 * clica num botao que vai deixar de existir. Foi assim que o E2E de planos
 * ficou 30s esperando um botao ja' desmontado.
 */
export function useFeatureFlagState(flagKey: string): { enabled: boolean; carregando: boolean } {
  const { data, isPending } = useQuery({
    queryKey: ["feature-flag", flagKey],
    queryFn: async () => {
      const { data } = await supabase
        .from("feature_flags")
        .select("enabled")
        .eq("flag_key", flagKey)
        .single();
      return data?.enabled ?? false;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
  return { enabled: data ?? false, carregando: isPending };
}

export function useFeatureFlag(flagKey: string): boolean {
  return useFeatureFlagState(flagKey).enabled;
}

/**
 * A/B flag: retorna true para Variante B (último char hex do UUID par: 0,2,4,6,8,a,c,e).
 * Determinístico por usuário. Só ativo quando a flag está habilitada.
 */
export function useABFlag(flagKey: string): boolean {
  const { user } = useAuth();
  const flagEnabled = useFeatureFlag(flagKey);
  if (!flagEnabled || !user) return false;
  const lastChar = user.id.slice(-1).toLowerCase();
  return "02468ace".includes(lastChar);
}
